import { AsyncPipe } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, HostBinding, Inject, OnInit } from '@angular/core';
import { Router, RouterOutlet } from '@angular/router';
import {
  MSAL_GUARD_CONFIG,
  MsalGuardConfiguration,
  MsalService,
} from '@azure/msal-angular';
import { TranslateService } from '@ngx-translate/core';
import { OAuthService } from 'angular-oauth2-oidc';
import { JwksValidationHandler } from 'angular-oauth2-oidc-jwks';
import { NgHttpLoaderComponent } from 'ng-http-loader';
import { NgxSpinnerModule } from 'ngx-spinner';
import { EMPTY, Observable, timer } from 'rxjs';
import { catchError, filter, switchMap, take, tap } from 'rxjs/operators';
import { SubSink } from 'subsink';
import { environment } from '../environments/environment';
import { AuthService } from './core/auth/auth.service';
import { AutoUnsubscribe } from './core/auto-unsubscribe';
import { LanguageService } from './core/language.service';
import { PruningTranslationLoader } from './core/pruning-translation-loader ';
import { UserInfo, UserInfoService } from './core/user-info.service';
import { AjsFrameComponent } from './shared/ajs-frame/ajs-frame.component';
import { TenderRouteNames } from './shared/routing.constants';
import { TopNavigationBarComponent } from './shared/top-navigation-bar/top-navigation-bar.component';

@AutoUnsubscribe()
@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  imports: [
    NgHttpLoaderComponent,
    TopNavigationBarComponent,
    NgxSpinnerModule,
    AjsFrameComponent,
    RouterOutlet,
    AsyncPipe,
  ],
})
export class AppComponent implements OnInit {
  @HostBinding('attr.app-version') appVersionAttr = environment.appVersion;

  subs = new SubSink();

  constructor(
    @Inject(MSAL_GUARD_CONFIG)
    private msalGuardConfig: MsalGuardConfiguration,
    private msalAuthService: MsalService,
    private languageService: LanguageService,
    private translateService: TranslateService,
    private authService: AuthService,
    private userInfoService: UserInfoService,
    private http: HttpClient,
    private oauthService: OAuthService,
    private router: Router
  ) {
    this.setupApiTokenRefresh();
    this.configureOidc();

    // redirect to invitation publish page after simap-login
    this.subs.sink = this.oauthService.events
      .pipe(
        filter(e => e.type === 'token_received'),
        take(1) // only take the first (most recent) token_received event
      )
      .subscribe(e => {
        if (this.oauthService.state) {
          const tmp = this.oauthService.state.split('_', 2);

          switch (tmp[1]) {
            case TenderRouteNames.CommunicateAward:
              this.router.navigate([
                TenderRouteNames.ModuleBase,
                tmp[0],
                TenderRouteNames.CommunicateAward,
              ]);
              break;
            default:
              this.router.navigate([
                TenderRouteNames.ModuleBase,
                tmp[0],
                TenderRouteNames.Publish,
              ]);
              break;
          }
        }
      });
  }

  private setupApiTokenRefresh() {
    const tokenRefreshInterval = 1000 * 60 * 3; // 3 minutes
    this.subs.sink = timer(tokenRefreshInterval, tokenRefreshInterval) // refresh every 3 minutes, starting after 3 minutes
      .pipe(
        switchMap(() =>
          this.authService.isAuthenticated().pipe(
            take(1), // only take the first value, otherwise we end in an infinite loop
            switchMap(isAuthenticated => {
              if (isAuthenticated) return this.authService.refreshAccessToken(); // refresh access token if user is authenticated
              return EMPTY;
            })
          )
        ),
        catchError(() => {
          console.error(
            'setupApiTokenRefresh: Failed to refresh access token, logging out'
          );

          this.authService.signOut(); // sign out if refresh fails
          return EMPTY;
        })
      )
      .subscribe();
  }

  private configureOidc() {
    if (!environment.simapOAuthConfig.enabled) return;

    this.oauthService.configure(environment.simapOAuthConfig);
    this.oauthService.tokenValidationHandler = new JwksValidationHandler();
    this.oauthService.setupAutomaticSilentRefresh();
    this.oauthService.loadDiscoveryDocumentAndTryLogin();
  }

  public get isAuthenticated(): Observable<boolean> {
    return this.authService.isAuthenticated();
  }

  public get userInfo(): Observable<UserInfo | undefined> {
    return this.userInfoService.getUserInfo();
  }

  ngOnInit(): void {
    this.msalAuthService.handleRedirectObservable().subscribe();
    this.languageService.usePreferredLanguage();

    this.subs.sink = this.userInfo
      .pipe(
        tap(userInfo => {
          this.translateService.currentLoader = new PruningTranslationLoader(
            this.http,
            './assets/i18n/',
            '.json',
            userInfo
          );
        }),
        switchMap(() => this.languageService.currentLanguage)
      )
      .subscribe(s => {
        this.translateService.reloadLang(s.lang);
      });
  }
}
